/**
 * TicketChatComponents — Mobile-optimised shared chat components for support tickets.
 *
 * Provides ChatBubble (with message grouping, long-press actions), JumpToLatest,
 * AttachmentCard, ReactionPicker, TypingIndicator, ChatMessageInput (with camera
 * capture, drag-and-drop, upload progress), AutoScrollManager, and EmptyChatState.
 *
 * Designed for mobile-first with safe-area insets, adequate touch targets
 * (min 44×44 px), keyboard-aware layouts, and smooth animations.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import {
  X, Send, Loader, Paperclip, FileText, Image, File, Download,
  Eye, SmilePlus, Camera, ChevronDown, ChevronUp, Copy, Trash2, Edit3,
  MessageSquare, Reply, Upload, FileImage, FileSpreadsheet,
  ZoomIn, CheckCircle2, AlertCircle, RefreshCw, Clock,
  ExternalLink, Search, User, MoreHorizontal, FileX, Lock, CornerDownRight,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import { workspaceHeader } from '../../lib/workspaces';
import { normalizeTicketAttachment, ticketAttachmentAccessError, withTicketAttachmentContext, type TicketAttachment } from '../../lib/ticketAttachments';
import '../messaging/messaging.css';
import { messageBodyText } from '../messaging/utils';
import type { TicketComment, CommentAttachment, TicketReaction } from '../../types';
import Modal from './Modal';
import Avatar from './Avatar';
import { useModalLayer } from '../../lib/modalStack';

/* ── Inline animations ── */

export const SLIDE_UP_STYLE = `
@keyframes slide-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.animate-slide-up {
  animation: slide-up 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.18s ease-out both;
}
`;

/* ── Shared style singleton (injected once at module level) ── */

let _styleInjected = false;
function ensureStyleInjected() {
  if (_styleInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.textContent = SLIDE_UP_STYLE;
  document.head.appendChild(el);
  _styleInjected = true;
}

/* ── Helpers ── */

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diff = now - then;
  const secs = Math.floor(diff / 1000);
  if (secs < 10) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function formatFullTime(isoString: string): string {
  if (!isoString) return '';
  // Exact date + time, resolved to the viewer's local timezone.
  return new Date(isoString).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatExactDateTime(isoString: string): string {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('en-GB', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateLine(isoString: string): string {
  const d = new Date(isoString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

export function getInitials(name?: string): string {
  if (!name || !name.trim()) return '';
  return name.trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

export interface ParticipantAvatarProps {
  name?: string;
  photoUrl?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  icon?: React.ReactNode;
}

/**
 * @deprecated Thin alias over the shared <Avatar/> (src/components/shared/Avatar.tsx).
 * Kept so the existing ticket call sites keep working; new code should import
 * `Avatar` directly. All four ticket surfaces render through the same component
 * so a person can no longer look different in the list vs. the chat vs. details.
 */
export const ParticipantAvatar = React.memo(function ParticipantAvatar({
  name,
  photoUrl,
  size = 'md',
  className = '',
  icon,
}: ParticipantAvatarProps) {
  return <Avatar name={name} photoUrl={photoUrl} size={size} className={className} icon={icon} />;
});

function getFileIcon(mimeType: string, className = 'h-5 w-5') {
  if (mimeType.startsWith('image/')) return <FileImage className={className} />;
  if (mimeType === 'application/pdf') return <FileText className={className} />;
  if (mimeType.includes('word')) return <FileSpreadsheet className={className} />;
  if (mimeType === 'text/plain') return <File className={className} />;
  return <File className={className} />;
}

function getFileColor(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'from-pink-400 to-rose-500';
  if (mimeType === 'application/pdf') return 'from-red-400 to-rose-600';
  if (mimeType.includes('word')) return 'from-blue-400 to-indigo-600';
  if (mimeType === 'text/plain') return 'from-slate-400 to-slate-600';
  return 'from-slate-400 to-slate-600';
}

function getFileLabel(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.includes('word')) return 'Word';
  if (mimeType === 'text/plain') return 'Text';
  return 'File';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ── Constants ── */

// MUST stay in sync with TICKET_ALLOWED_EXTENSIONS in server.ts. The server is
// the real security boundary (it re-validates extension + sniffed MIME), but if
// this client list is NARROWER than the server's, users are silently blocked
// from formats the server accepts — which is what had happened here.
export const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif';
export const ALLOWED_EXTENSIONS_SET: ReadonlySet<string> = new Set(ALLOWED_EXTENSIONS.split(','));
export const ALLOWED_MIME_TYPES = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,application/rtf,text/rtf,image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif';
export const ALLOWED_MIME_TYPES_SET: ReadonlySet<string> = new Set(ALLOWED_MIME_TYPES.split(','));

/** Exact-match extension check. Never use substring `.includes()` on a joined
 *  allow-list string — that lets `.t` pass as a substring of `.txt`. */
export function isAllowedExtension(ext: string): boolean {
  return ALLOWED_EXTENSIONS_SET.has(ext);
}

export function isAllowedMimeType(mimeType: string): boolean {
  return ALLOWED_MIME_TYPES_SET.has(mimeType);
}
export const REACTION_CHOICES: TicketReaction[] = ['👍', '❤️', '😊', '🎉', '😢', '🙏'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/* ── Auth token for signed attachment URLs ── */

let _cachedToken: string | null = null;
let _tokenExpiry = 0;

export async function getSignedToken(): Promise<string | null> {
  if (_cachedToken && Date.now() < _tokenExpiry) return _cachedToken;
  try {
    const user = auth.currentUser;
    if (!user) return null;
    const token = await user.getIdToken();
    if (token) {
      _cachedToken = token;
      _tokenExpiry = Date.now() + 25 * 60 * 1000;
    }
    return token;
  } catch { return null; }
}

/**
 * A URL the browser can already display on its own — our own object URL from a
 * completed fetch, or an inline data URL. These must never be re-fetched through
 * the authenticated pipeline.
 */
export function isDirectPreviewUrl(url: string): boolean {
  return /^(blob:|data:)/i.test(url || '');
}

/**
 * Why an attachment fetch failed.
 *
 * Surfaced to the user instead of a generic "try again": telling someone to hit
 * Retry is useless advice when the real cause is a permission or a deleted file.
 */
export type AttachmentFailure = 'no-session' | 'forbidden' | 'not-found' | 'processing' | 'storage-unavailable' | 'network' | 'unknown';

export const ATTACHMENT_FAILURE_COPY: Record<AttachmentFailure, string> = {
  'no-session': 'Your session has expired. Reload the page, then try again.',
  forbidden: 'You do not have permission to open this file on this ticket.',
  'not-found': 'This file is no longer stored on the server. Ask the sender to attach it again.',
  processing: 'This file is still being checked. Try again in a moment.',
  // Distinct from 'not-found' on purpose: the file is NOT gone — the server has
  // no usable storage bucket, so it cannot look. Telling the user (or the next
  // person reading a bug report) that the file was deleted sends everyone down
  // the wrong path, which is exactly what happened here.
  'storage-unavailable': 'The server’s file storage is not configured, so this file cannot be opened right now. This is a server problem — the file itself is not missing.',
  network: 'The file could not be reached — check your connection and retry.',
  unknown: 'The server could not return this file. Retry, or download it instead.',
};

/** Result of an authenticated attachment fetch. */
export type AttachmentFetchResult = {
  blobUrl: string | null;
  failure: AttachmentFailure | null;
  status?: number;
};

/**
 * Remembered attachment failures.
 *
 * A ticket thread mounts one fetch per attachment, and ordinary re-renders mount
 * them again — so a genuinely missing object produced a 404 (plus a full React
 * stack in the console, per request) on every remount. That buried the errors
 * that actually mattered and re-requested a file known not to exist.
 *
 * A failure is remembered briefly, so repeated renders show the failure UI
 * without touching the network again. Only FAILURES are remembered: a success
 * always returns a fresh object URL, because two mounts sharing one would break
 * the moment either revokes it on unmount.
 */
const ATTACHMENT_FAILURE_TTL_MS = 30_000;
const attachmentFailures = new Map<string, { result: AttachmentFetchResult; at: number }>();

/** Forgets a remembered failure so the next fetch really hits the network (Retry). */
export function clearAttachmentFetchState(url: string): void {
  attachmentFailures.delete(url);
}

/** fetchAsBlobUrl with a machine-readable reason for the failure. */
export async function fetchAsBlobUrlDetailed(url: string): Promise<AttachmentFetchResult> {
  if (!url) return { blobUrl: null, failure: 'not-found' };
  if (isDirectPreviewUrl(url)) return { blobUrl: url, failure: null };

  const remembered = attachmentFailures.get(url);
  if (remembered && Date.now() - remembered.at < ATTACHMENT_FAILURE_TTL_MS) return remembered.result;

  const token = await getSignedToken().catch(() => null);
  if (!token) return { blobUrl: null, failure: 'no-session' };
  const remember = (result: AttachmentFetchResult): AttachmentFetchResult => {
    attachmentFailures.set(url, { result, at: Date.now() });
    return result;
  };
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
    });
    if (res.status === 401 || res.status === 403) return remember({ blobUrl: null, failure: 'forbidden', status: res.status });
    if (res.status === 404) return remember({ blobUrl: null, failure: 'not-found', status: res.status });
    if (res.status === 425) return remember({ blobUrl: null, failure: 'processing', status: res.status });
    // 503 storage_unavailable: the server has no usable bucket. Reported as its
    // own kind so the UI (and any bug report) can tell a configuration failure
    // apart from a deleted file.
    if (res.status === 503) return remember({ blobUrl: null, failure: 'storage-unavailable', status: res.status });
    if (!res.ok) return remember({ blobUrl: null, failure: 'unknown', status: res.status });
    return { blobUrl: URL.createObjectURL(await res.blob()), failure: null, status: res.status };
  } catch {
    return { blobUrl: null, failure: 'network' };
  }
}

/** True when an attachment should open in the PDF preview surface. */
export function isPdfAttachment(att?: { mimeType?: string; fileName?: string } | null, url = ''): boolean {
  const mime = (att?.mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return true;
  const name = (att?.fileName || url || '').toLowerCase();
  return /\.pdf(\?|#|$)/.test(name);
}

/** True when an attachment should open in the image lightbox. */
export function isImageAttachment(att?: { mimeType?: string; fileName?: string } | null, url = ''): boolean {
  const mime = (att?.mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = (att?.fileName || url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|avif|bmp|heic|heif|svg)(\?|#|$)/.test(name);
}

/** Fetches a URL with auth headers and returns an object URL for preview use. */
export async function fetchAsBlobUrl(url: string): Promise<string | null> {
  // Thin wrapper over the detailed fetch so every caller — thumbnails, the
  // documents grid, previews — shares ONE code path and one failure cache.
  //
  // The behaviour worth preserving: a URL that is ALREADY displayable
  // (blob:/data: — e.g. a thumbnail the caller decoded itself) is returned as-is.
  // That check is the fix for "the preview popup isn't working": the old code
  // re-fetched those through the authenticated path, `getSignedToken()` returns
  // null in perfectly normal situations (no cached token yet, a transient
  // getIdToken failure), and the preview then reported "Image unavailable" for a
  // file it was holding in memory. Attaching an Authorization header to a blob:
  // request is meaningless anyway — it is same-origin.
  const { blobUrl } = await fetchAsBlobUrlDetailed(url);
  return blobUrl;
}

/** Triggers a download of the file at the given URL using auth headers. */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  try {
    // Same short-circuit as the preview: a blob:/data: URL needs no token, and
    // requiring one made "Download" fail for exactly the same in-memory files.
    const direct = isDirectPreviewUrl(url);
    const token = direct ? null : await getSignedToken();
    if (!direct && !token) throw new Error('Not authenticated');

    const response = await fetch(url, {
      headers: direct ? undefined : { Authorization: `Bearer ${token}`, ...workspaceHeader() },
    });

    if (!response.ok) {
      // 503 is the server saying "I have no storage bucket" — far more useful to
      // whoever reads the error than a bare status code.
      if (response.status === 503) throw new Error('Download failed: the server has no file storage configured (503).');
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  } catch (err) {
    console.error('Download error:', err);
    throw err;
  }
}

/**
 * Preserves the ticket context for the upload proxy. This is required for
 * legacy comments whose storage object exists but whose uploadOwnership record
 * was never written. The server verifies the attachment belongs to the hinted
 * ticket before using the value to resolve a storage path.
 */
export function ticketAttachmentUrl(url: string, ticketId?: string): string {
  return withTicketAttachmentContext(url, ticketId);
}

/** Hook: returns a function to get a blob URL for an attachment path (for preview). */
export function useBlobUrl() {
  return React.useCallback(async (url: string): Promise<string | null> => {
    return await fetchAsBlobUrl(url);
  }, []);
}

/** Hook: returns a function to download an attachment. */
export function useAttachmentDownload() {
  return React.useCallback(async (url: string, filename?: string) => {
    await downloadFile(url, filename);
  }, []);
}

// Keep the old named export for backward compatibility during migration,
// but mark it as deprecated so consumers know to migrate.
export const useSignedUrl = useBlobUrl;
export const addTokenToUrl = (url: string, token: string): string => {
  console.warn('addTokenToUrl is deprecated and insecure; use useBlobUrl/downloadFile instead');
  return url;
};

/*
 * Admin / internal-staff chat shares the platform messaging design system:
 * bubbles, radii and avatar colours now resolve from the same brand tokens the
 * applicant, employer and referrer threads use (src/components/messaging).
 * Previously admin bubbles were hardcoded indigo, applicant bubbles brand-blue
 * and referrer bubbles indigo-600 — three different "own message" colours.
 */
const AVATOR_COLORS: Record<string, string> = {
  admin: 'msg-avatar-admin',
  applicant: 'msg-avatar-applicant',
  internal: 'msg-avatar-internal',
};

/* ── Date Separator (messenger-style) ── */

export const DateSeparator = React.memo(({ date }: { date: string }) => (
  <div className="flex items-center justify-center my-4" role="separator" aria-label={formatDateLine(date)}>
    <span className="text-[11px] font-semibold text-slate-400 px-3 py-1 bg-black/[0.04] rounded-full select-none">
      {formatDateLine(date)}
    </span>
  </div>
));

DateSeparator.displayName = 'DateSeparator';

/* ── File Type Badge (icon + gradient) ── */

const FileTypeBadge = React.memo(({ mimeType }: { mimeType: string }) => (
  <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${getFileColor(mimeType)} flex items-center justify-center shadow-sm`}>
    {getFileIcon(mimeType, 'h-5 w-5 text-white')}
  </div>
));

FileTypeBadge.displayName = 'FileTypeBadge';

/* ── PDF Preview Modal ── */

function PdfPreviewModal({ url, fileName, onClose }: { url: string; fileName: string; onClose: () => void }) {
  const [loadError, setLoadError] = React.useState(false);
  const [failure, setFailure] = React.useState<AttachmentFailure | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const [checking, setChecking] = React.useState(true);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const isIOS = React.useRef(typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent));

  React.useEffect(() => {
    let cancelled = false;
    setChecking(true);
    setLoadError(false);
    setFailure(null);
    setBlobUrl(null);
    // Detailed fetch so the failure screen can explain WHY, and so Retry
    // (attempt++) genuinely re-runs the request instead of doing nothing.
    fetchAsBlobUrlDetailed(url).then(({ blobUrl: next, failure: reason }) => {
      if (cancelled) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      if (next) setBlobUrl(next);
      else { setLoadError(true); setFailure(reason || 'unknown'); }
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [url, attempt]);

  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
      if (doc && doc.body && (doc.body.innerText || '').includes('File not found')) {
        setLoadError(true);
      }
    } catch { /* cross-origin — silently ignored */ }
  };

  // Release the object URL we created (not a caller-supplied blob URL).
  React.useEffect(() => () => {
    if (blobUrl && blobUrl !== url && blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  }, [blobUrl, url]);

  return (
    <Modal onClose={onClose} ariaLabel="PDF Preview">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 text-red-500 shrink-0" />
            <span className="text-sm font-bold text-slate-800 truncate">{fileName}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Some browser/enterprise configurations refuse to render a blob:
                PDF inside an iframe. A direct tab is the reliable escape hatch,
                and it is the fastest way to read a long document anyway. */}
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noreferrer"
                className="hidden min-h-[44px] items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 transition-all hover:bg-slate-200 sm:flex"
                title="Open this PDF in a new browser tab"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Open in new tab
              </a>
            )}
            <button
              onClick={async () => {
                try {
                  await downloadFile(url, fileName);
                } catch (err) {
                  // downloadFile already shows console error
                }
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-brand bg-brand-active rounded-lg transition-all cursor-pointer min-h-[44px]"
              aria-label={`Download ${fileName}`}
              title={`Download ${fileName}`}
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center" type="button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-slate-100 overflow-hidden flex items-center justify-center">
          {loadError ? (
            <div className="flex flex-col items-center gap-3 px-6 text-center text-slate-500">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-200/80">
                <AlertCircle className="h-7 w-7 text-slate-500" aria-hidden="true" />
              </span>
              <p className="text-sm font-semibold">
                Can&apos;t show this file in the viewer
              </p>
              {/* Say WHY. A generic "could not be loaded" hides permission
                  errors and deleted files, which Retry can never fix. */}
              <p className="max-w-sm text-xs leading-relaxed">
                {ATTACHMENT_FAILURE_COPY[failure || 'unknown']}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {(failure === 'network' || failure === 'unknown' || failure === 'no-session' || failure === 'processing') && (
                  <button
                    type="button"
                    onClick={() => { clearAttachmentFetchState(url); setAttempt(n => n + 1); }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 transition-all hover:bg-slate-100 cursor-pointer"
                    aria-label={`Retry loading ${fileName}`}
                  >
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Retry
                  </button>
                )}
                <button
                  type="button"
                  onClick={async () => { try { await downloadFile(url, fileName); } catch { /* downloadFile logs */ } }}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-xs font-bold text-white transition-all hover:opacity-90 cursor-pointer"
                  aria-label={`Download ${fileName}`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download
                </button>
              </div>
              <p className="text-[10px] font-semibold text-slate-400">
                {fileName}{failure === 'forbidden' && ' · downloads are also restricted'}
              </p>
            </div>
          ) : checking ? (
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <Loader className="h-6 w-6 animate-spin" />
              <p className="text-xs">Loading preview...</p>
            </div>
          ) : isIOS.current ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <FileText className="h-12 w-12 text-red-400" />
              <p className="text-sm font-semibold text-slate-500">PDF Preview</p>
              <p className="text-xs text-slate-400 text-center max-w-xs">Tap the button below to open this PDF in the system viewer.</p>
              <button
                onClick={async () => {
                  try {
                    await downloadFile(url, fileName);
                  } catch (err) {
                    // downloadFile already shows console error
                  }
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 btn-primary  text-white text-sm font-bold rounded-xl transition-colors cursor-pointer min-h-[44px]"
              >
                <ExternalLink className="h-4 w-4" />
                Open PDF
              </button>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={blobUrl || ''}
              title={fileName}
              className="w-full h-full min-h-[70vh]"
              style={{ border: 'none' }}
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ── Image Lightbox Modal ── */

function ImageLightbox({ url, fileName, onClose }: { url: string; fileName: string; onClose: () => void }) {
  const [loadError, setLoadError] = React.useState(false);
  const [failure, setFailure] = React.useState<AttachmentFailure | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    setFailure(null);
    fetchAsBlobUrlDetailed(url).then(({ blobUrl: next, failure: reason }) => {
      if (cancelled) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      setBlobUrl(next);
      if (!next) { setLoadError(true); setFailure(reason || 'unknown'); }
    });
    return () => { cancelled = true; };
  }, [url, attempt]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Release object URLs that WE created — never the caller's blob URL — so
  // clicking through a thread of previews doesn't leak a decoded image each time.
  React.useEffect(() => () => {
    if (blobUrl && blobUrl !== url && blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  }, [blobUrl, url]);

  return (
    <Modal onClose={onClose} backdropClassName="bg-black/80 p-0 sm:p-4">
      <div className="relative w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
          type="button"
          aria-label="Close preview"
        >
          <X className="h-5 w-5" />
        </button>
        <button
          onClick={async () => {
            try {
              await downloadFile(url, fileName);
            } catch (err) {
              // downloadFile already shows console error
            }
          }}
          className="absolute top-4 right-16 z-10 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
          title="Download"
        >
          <Download className="h-5 w-5" />
        </button>
        {loadError ? (
          <div className="flex flex-col items-center gap-3 text-white/70">
            <AlertCircle className="h-12 w-12" aria-hidden="true" />
            <p className="text-sm font-semibold">Can&apos;t show this image</p>
            <p className="max-w-sm text-center text-xs leading-relaxed text-white/60">
              {ATTACHMENT_FAILURE_COPY[failure || 'unknown']}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {(failure === 'network' || failure === 'unknown' || failure === 'no-session' || failure === 'processing') && (
                <button
                  type="button"
                  onClick={() => { clearAttachmentFetchState(url); setAttempt(n => n + 1); }}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-white/30 px-3.5 py-2 text-xs font-bold text-white transition-all hover:bg-white/15 cursor-pointer"
                  aria-label={`Retry loading ${fileName}`}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Retry
                </button>
              )}
              <button
                type="button"
                onClick={async () => {
                  try {
                    await downloadFile(url, fileName);
                  } catch {
                    // downloadFile already logs
                  }
                }}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-white/15 px-3.5 py-2 text-xs font-bold text-white transition-all hover:bg-white/25 cursor-pointer"
                aria-label={`Download ${fileName}`}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download file
              </button>
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/40">{fileName}</p>
          </div>
        ) : blobUrl ? (
          <img
            src={blobUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain p-4"
            onError={() => setLoadError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/60">
            <Loader className="h-6 w-6 animate-spin" />
            <p className="text-xs">Loading preview...</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ── Attachment cards — ONE treatment, four call-site aliases ─────────────────
 * Every attachment in the product now renders through AttachmentFileCard, which
 * is plain .msg-file markup (see messaging.css). Because .msg-file sizes and
 * tints itself from `currentColor`, the SAME component reads correctly on a
 * white received bubble, on the brand-filled own bubble and on the warm
 * internal-note bubble.
 *
 * That is what removes the old split, where four near-identical copies drifted
 * into a "purple PDF card" and a "white failed-preview card" that no longer
 * looked like the same component.
 */

/**
 * Middle-truncate a filename while ALWAYS keeping the extension visible.
 *
 *   "2026 All Inclusive ISATS (1)-Daina.pdf" → "2026 All Incl…-Daina.pdf"
 *
 * CSS `truncate` only clips the END, which is precisely where the extension and
 * the distinguishing tail live — that is how a card ended up reading
 * "85ac085...". The full name is always in the tooltip.
 */
export function middleTruncateFileName(name: string, max = 38): string {
  const value = (name || '').trim();
  if (value.length <= max) return value;
  const dot = value.lastIndexOf('.');
  const ext = dot > 0 && value.length - dot <= 6 ? value.slice(dot) : '';
  const stem = ext ? value.slice(0, dot) : value;
  const room = Math.max(8, max - ext.length - 1);
  const head = Math.max(4, Math.ceil(room * 0.6));
  const tail = Math.max(2, room - head);
  return `${stem.slice(0, head)}…${stem.slice(-tail)}${ext}`;
}

const AttachmentFileCard = React.memo(function AttachmentFileCard({
  att, onPreview, onAccent = false,
}: { att: CommentAttachment & Partial<TicketAttachment>; onPreview?: (url: string) => void; onAccent?: boolean }) {
  const accessError = ticketAttachmentAccessError(normalizeTicketAttachment({
    fileId: att.fileId || att.id || '',
    fileName: att.fileName || '',
    fileSize: att.fileSize || 0,
    mimeType: att.mimeType || '',
    url: att.url || '',
    status: att.status || 'available',
  }));
  const isPdf = isPdfAttachment(att, att.url);
  const isImage = isImageAttachment(att, att.url);
  const canPreview = isPdf || isImage;

  const handleDownload = async () => {
    try {
      await downloadFile(att.url, att.fileName);
    } catch {
      // downloadFile already logs; keep the card in place so nothing bounces.
    }
  };

  return (
    <div className={['msg-file', onAccent ? 'msg-file--on-accent' : ''].join(' ')}>
      <span className="msg-file-icon" aria-hidden="true">
        <FileTypeBadge mimeType={att.mimeType} />
      </span>
      <span className="msg-file-body">
        <span className="msg-file-name" title={att.fileName}>{middleTruncateFileName(att.fileName)}</span>
        <span className="msg-file-sub">{accessError === 'processing' ? 'Security scan in progress' : accessError === 'blocked' ? 'Blocked by security scan' : accessError === 'missing' ? 'File unavailable' : `${formatFileSize(att.fileSize)} · ${getFileLabel(att.mimeType)}`}</span>
      </span>
      {accessError ? (
        <span className="msg-file-view" aria-label={accessError === 'processing' ? 'File is still processing' : 'File is unavailable'} title={accessError === 'processing' ? 'Preview and download are available once scanning finishes' : 'This file cannot be opened'}>
          {accessError === 'processing' ? <Clock className="h-3.5 w-3.5" aria-hidden="true" /> : <FileX className="h-3.5 w-3.5" aria-hidden="true" />}
          <span className="hidden sm:inline">{accessError === 'processing' ? 'Processing' : 'Unavailable'}</span>
        </span>
      ) : canPreview ? (
        <button
          type="button"
          onClick={() => onPreview?.(att.url)}
          className="msg-file-view"
          title={isPdf ? 'Preview this document' : 'Preview this image'}
          aria-label={`Preview ${att.fileName}`}
        >
          {isPdf
            ? <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            : <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />}
          <span className="hidden sm:inline">View</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={handleDownload}
          className="msg-file-view"
          title={`Download ${att.fileName}`}
          aria-label={`Download ${att.fileName}`}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">Download</span>
        </button>
      )}
    </div>
  );
});

AttachmentFileCard.displayName = 'AttachmentFileCard';

/** Neutral surface — documents panel and light contexts. */
export const AttachmentCard = React.memo(({ att, onPreview }: { att: CommentAttachment; onPreview?: (url: string) => void }) => (
  <AttachmentFileCard att={att} onPreview={onPreview} />
));

AttachmentCard.displayName = 'AttachmentCard';

/** Inside the brand-filled own bubble. */
export const AttachmentCardLight = React.memo(({ att, onPreview }: { att: CommentAttachment; onPreview?: (url: string) => void }) => (
  <AttachmentFileCard att={att} onPreview={onPreview} onAccent />
));

AttachmentCardLight.displayName = 'AttachmentCardLight';

/** Documents panel, "mine" grouping. */
export const AttachmentCardPanelOwn = React.memo(({ att, onPreview }: { att: CommentAttachment; onPreview?: (url: string) => void }) => (
  <AttachmentFileCard att={att} onPreview={onPreview} />
));

AttachmentCardPanelOwn.displayName = 'AttachmentCardPanelOwn';

/** Received (applicant / teammate) bubbles. */
export const AttachmentCardReceived = React.memo(({ att, onPreview }: { att: CommentAttachment; onPreview?: (url: string) => void }) => (
  <AttachmentFileCard att={att} onPreview={onPreview} />
));

AttachmentCardReceived.displayName = 'AttachmentCardReceived';

/* ── Image Attachment (thumbnail grid) ── */

const ImageAttachment = React.memo(({ url, fileName, onPreview }: { url: string; fileName: string; onPreview: (url: string) => void }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoaded(false);
    setError(false);
    setBlobUrl(null);
    fetchAsBlobUrl(url).then((nextUrl) => {
      if (cancelled) {
        if (nextUrl) URL.revokeObjectURL(nextUrl);
        return;
      }
      if (nextUrl) {
        objectUrl = nextUrl;
        setBlobUrl(nextUrl);
      }
      else setError(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, attempt]);
  if (error) {
    // Designed error state: real icon + caption + a recovery action. Never a
    // bare broken-image glyph or floating "Failed to load" text.
    return (
      <div
        className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
        role="group"
        aria-label={`Image unavailable: ${fileName}`}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-200/70">
          <FileImage className="h-4 w-4 text-slate-500" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-semibold text-slate-700" title={fileName}>{fileName}</span>
          <span className="block text-[10px] text-slate-500">Preview unavailable</span>
        </span>
        <button
          type="button"
          onClick={async () => {
            setBusy(true);
            try { await downloadFile(url, fileName); } catch { /* browser surfaces the failure */ }
            finally { setBusy(false); }
          }}
          disabled={busy}
          className="inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-700 transition-all hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
          aria-label={`Download ${fileName}`}
        >
          {busy ? <Loader className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download
        </button>
        <button
          type="button"
          onClick={() => { clearAttachmentFetchState(url); setAttempt(value => value + 1); }}
          className="inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-bold text-slate-600 transition-all hover:bg-slate-200 cursor-pointer"
        >
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[200px] sm:max-w-[220px] overflow-hidden rounded-xl border border-white/20 bg-slate-200/40">
      <button
        onClick={() => blobUrl && onPreview(blobUrl)}
        className="relative block w-full overflow-hidden bg-slate-200/40 transition-opacity hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-brand-primary aspect-[4/3] max-h-[140px]"
        type="button"
        aria-label={`Open image ${fileName}`}
        title={fileName}
        disabled={!blobUrl}
      >
      {!loaded && (
        <span className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200/70 to-slate-300/50" aria-hidden="true" />
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={fileName}
          className={`relative w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
      </button>
      {loaded && (
        <div className="flex min-w-0 items-center gap-2 border-t border-white/20 bg-black/15 px-2 py-1.5">
          <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-white" title={fileName}>{fileName}</span>
          <button type="button" onClick={() => downloadFile(url, fileName).catch(() => {})} className="shrink-0 rounded-md p-1 text-white/80 hover:bg-white/15 hover:text-white" aria-label={`Download ${fileName}`} title="Download"><Download className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
});

ImageAttachment.displayName = 'ImageAttachment';

/**
 * Auth-aware thumbnail for the ticket "Documents" image grid.
 *
 * A raw `<img src={attachmentUrl}>` cannot authenticate: /api/uploads/* requires
 * a bearer token, and the browser sends none for a plain image request (the old
 * `addTokenToUrl` that used to append one is a deprecated no-op now). So every
 * grid thumbnail 401'd and rendered the browser's broken-image glyph — that was
 * Screenshot 1 (grey box, filename text sitting on top of a broken icon).
 * Fetch the bytes with the caller's token instead, and only draw the
 * filename/meta caption once the image has actually loaded, so text can never
 * overlap a broken image.
 */
const AuthThumb = React.memo(({
  url, fileName, meta, alt, onClick,
}: { url: string; fileName: string; meta?: string; alt: string; onClick: (resolvedUrl: string) => void }) => {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoaded(false);
    setError(false);
    setBlobUrl(null);
    fetchAsBlobUrl(url).then((nextUrl) => {
      if (cancelled) {
        if (nextUrl) URL.revokeObjectURL(nextUrl);
        return;
      }
      if (nextUrl) {
        objectUrl = nextUrl;
        setBlobUrl(nextUrl);
      } else setError(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, attempt]);

  const resolvedUrl = blobUrl || url;

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
      <button
        onClick={() => blobUrl && onClick(resolvedUrl)}
        className="group relative block w-full overflow-hidden bg-slate-100 transition-all hover:shadow-md cursor-pointer aspect-[4/3]"
        title={meta ? `${fileName} — ${meta}` : fileName}
        type="button"
        disabled={!blobUrl}
      >
      {!loaded && !error && (
        <span className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200/70 to-slate-300/50" aria-hidden="true" />
      )}
      {error ? (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-100 px-2 text-center">
          <FileImage className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <span className="text-[11px] font-medium text-slate-400">Preview unavailable</span>
        </span>
      ) : blobUrl ? (
        <img
          src={blobUrl}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      ) : null}
      </button>
      <div className="flex min-w-0 items-center gap-1.5 px-2 py-1.5 text-left">
        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-slate-700" title={fileName}>{fileName}</span>
        {meta && <span className="shrink-0 truncate text-[11px] text-slate-400">{meta}</span>}
        {error && <>
          <button type="button" onClick={() => { clearAttachmentFetchState(url); setAttempt(value => value + 1); }} className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-200" aria-label={`Retry preview for ${fileName}`} title="Retry preview"><RefreshCw className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => downloadFile(url, fileName).catch(() => {})} className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-200" aria-label={`Download ${fileName}`} title="Download"><Download className="h-3.5 w-3.5" /></button>
        </>}
      </div>
    </div>
  );
});

AuthThumb.displayName = 'AuthThumb';

/* ── Reaction Picker ── */

export function ReactionPicker({
  onReact,
  onClose,
  isOwn,
}: {
  onReact: (reaction: TicketReaction) => void;
  onClose: () => void;
  isOwn?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; right?: number; left?: number } | null>(null);
  // SYS-11: register with the modal stack while open (was an invented
  // z-index 1000, below the modal base) so the picker always paints above
  // whatever hosts the chat.
  const layerZ = useModalLayer(pos !== null);

  React.useEffect(() => {
    const handler = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('touchstart', handler, { passive: true });
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose]);

  // Position from the in-place anchor (inside the bubble row, not clipped by overflow)
  React.useLayoutEffect(() => {
    requestAnimationFrame(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      // The anchor is a child inside [data-reaction-picker-root], rendered
      // immediately after the <button>, so its previous sibling IS the button.
      const btn = anchor.previousElementSibling as HTMLElement | null;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const below = rect.bottom + 8;
        const fitsBelow = below + 60 < window.innerHeight;
        if (fitsBelow) {
          setPos(isOwn ? { top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) } : { top: rect.bottom + 8, left: Math.min(Math.max(rect.left, 8), window.innerWidth - 300) });
        } else {
          setPos(isOwn ? { top: rect.top - 60, right: Math.max(8, window.innerWidth - rect.right) } : { top: rect.top - 60, left: Math.min(Math.max(rect.left, 8), window.innerWidth - 300) });
        }
      }
    });
  }, [isOwn]);

  return (
    <>
      {/* Zero-size anchor inside the bubble row — gives us unclipped coordinates */}
      <div ref={anchorRef} className="absolute w-0 h-0 pointer-events-none" />
      {pos && ReactDOM.createPortal(
        <div
          ref={ref}
          className="fixed"
          style={{ ...pos, zIndex: layerZ }}
        >
          <div
            className={`flex items-center gap-0.5 bg-white border border-slate-200 rounded-2xl px-2 py-1.5 shadow-xl ${isOwn ? 'flex-row-reverse' : ''}`}
            role="toolbar"
            aria-label="Reaction picker"
          >
            {REACTION_CHOICES.map(emoji => (
              <button
                key={emoji}
                onClick={() => { onReact(emoji); onClose(); }}
                className="text-xl hover:bg-slate-100 rounded-full px-2 py-1 transition-all cursor-pointer hover:scale-125 active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={`React with ${emoji}`}
                type="button"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/* ── Message Actions (long-press menu) ── */

interface MessageAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
}

interface MessageActionsMenuProps {
  actions: MessageAction[];
  onClose: () => void;
  isOwn?: boolean;
}

export function MessageActionsMenu({ actions, onClose, isOwn }: MessageActionsMenuProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; right?: number; left?: number } | null>(null);
  // SYS-11: register with the modal stack while open (was an invented
  // z-index 1000, below the modal base) so the menu always paints above
  // whatever hosts the chat.
  const layerZ = useModalLayer(pos !== null);

  // Outside-click handler — uses ref to the portal content
  React.useEffect(() => {
    const handler = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose]);

  React.useEffect(() => {
    requestAnimationFrame(() => { ref.current?.focus(); });
  }, []);

  // Position from the in-place anchor (inside the bubble row, not clipped by overflow)
  React.useLayoutEffect(() => {
    requestAnimationFrame(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const below = rect.bottom + 4;
      const menuHeight = actions.length * 48 + 24;
      const fitsBelow = below + menuHeight < window.innerHeight;
      if (fitsBelow) {
        setPos(isOwn ? { top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) } : { top: rect.bottom + 4, left: Math.min(Math.max(rect.left, 8), window.innerWidth - 180) });
      } else {
        setPos(isOwn ? { top: rect.top - menuHeight, right: Math.max(8, window.innerWidth - rect.right) } : { top: rect.top - menuHeight, left: Math.min(Math.max(rect.left, 8), window.innerWidth - 180) });
      }
    });
  }, [actions.length, isOwn]);

  return (
    <>
      {/* Zero-size anchor inside the bubble row — gives us unclipped coordinates */}
      <div ref={anchorRef} className="absolute w-0 h-0 pointer-events-none" />
      {pos && ReactDOM.createPortal(
        <div
          ref={ref}
          className="fixed"
          style={{ ...pos, zIndex: layerZ }}
          role="menu"
          aria-label="Message actions"
          tabIndex={-1}
        >
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl py-1.5 min-w-[160px] overflow-hidden">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={() => { action.action(); onClose(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer min-h-[44px]"
                role="menuitem"
                type="button"
              >
                <span className="text-slate-400 shrink-0">{action.icon}</span>
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/* ── Chat Bubble ──
 *
 * Modern messenger-style bubbles.
 * - Own messages: solid blue, right-aligned, NO avatar shown.
 * - Received messages: white card, left-aligned with avatar + name.
 *
 * FIX — horizontal scroll root causes and their solutions:
 * 1. The outer row must NOT use overflow-hidden — that was clipping absolute popups
 *    and causing browser scroll-width recalculation. Instead we use `contain: layout`
 *    (via inline style) so absolute children don't affect document scroll.
 * 2. The content column uses `min-w-0` + explicit `maxWidth` (via style prop) so
 *    flex children can never exceed the viewport width.
 * 3. The bubble itself uses `overflow-hidden break-words` + overflowWrap:'anywhere'
 *    so long unbreakable words wrap instead of expanding the bubble.
 * 4. The reactions row uses `max-w-full overflow-hidden` to prevent emoji buttons
 *    from stretching the row past the bubble width on small screens.
 * 5. MessageActionsMenu is rendered *outside* the bubble row (after the closing
 *    tag) so it can never affect the row's layout/scroll-width.
 */

interface ChatBubbleProps {
  comment: TicketComment & { status?: string };
  isOwn: boolean;
  ticketId: string;
  hideAvatar?: boolean;
  showHeader?: boolean;
  onReactionUpdate?: (commentId: string, reactions: Record<string, string[]>) => void;
  onReply?: (comment: TicketComment) => void;
  onJumpToMessage?: (commentId?: string, authorName?: string, body?: string) => void;
  onEdit?: (comment: TicketComment) => void;
  onDelete?: (comment: TicketComment) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  highlightQuery?: string;
  onRetry?: (comment: any) => void;
}

export const ChatBubble = React.memo(({
  comment, isOwn, ticketId, hideAvatar, showHeader,
  onReactionUpdate, onReply, onJumpToMessage, onEdit, onDelete, canEdit, canDelete,
  highlightQuery, onRetry,
}: ChatBubbleProps) => {
  // Ticket history contains a small number of legacy rich bodies. Normalise at
  // the boundary so rendering, copying and placeholder detection never call
  // string methods on an object/array.
  const bodyText = messageBodyText(comment.body);
  const [showReactions, setShowReactions] = React.useState(false);
  const [showActions, setShowActions] = React.useState(false);
  const [localReactions, setLocalReactions] = React.useState(comment.reactions || {});
  const [previewImage, setPreviewImage] = React.useState<{ url: string; fileName: string } | null>(null);
  const [previewPdf, setPreviewPdf] = React.useState<{ url: string; fileName: string } | null>(null);

  const signedAttachments = React.useMemo(
    () => (comment.attachments || [])
      .map(normalizeTicketAttachment)
      .filter((attachment): attachment is TicketAttachment => attachment !== null)
      .map((attachment) => ({ ...attachment, url: ticketAttachmentUrl(attachment.url, ticketId) })),
    [comment.attachments, ticketId],
  );
  const signedImageAttachments = React.useMemo(
    () => signedAttachments.filter(a => a.status === 'available' && isImageAttachment(a, a.url)),
    [signedAttachments]
  );
  const signedDocAttachments = React.useMemo(
    () => signedAttachments.filter(a => !(a.status === 'available' && isImageAttachment(a, a.url))),
    [signedAttachments]
  );

  // "(file attachment)" is the placeholder body a client sends when the message
  // is only a file. Never show that raw string: if the file is present the card
  // speaks for itself, and if it's missing we say so plainly instead of leaving
  // an unclickable placeholder.
  const hasAttachments = signedAttachments.length > 0;
  const isPlaceholderBody = !!bodyText && /^\(?\s*file attachment\s*\)?$/i.test(bodyText.trim());

  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => setShowActions(true), 500);
  };
  const handleTouchEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };
  React.useEffect(() => () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); }, []);

  const handleReaction = async (reaction: TicketReaction) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) { console.warn('[Reaction] No token — user not authenticated'); return; }
    try {
      const res = await fetch(`/api/tickets/${ticketId}/comments/${comment.commentId}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ reaction }),
      });
      if (res.ok) {
        const data = await res.json();
        setLocalReactions(data.reactions);
        onReactionUpdate?.(comment.commentId, data.reactions);
      } else {
        const errBody = await res.text().catch(() => '');
        console.error(`[Reaction] HTTP ${res.status}: ${errBody}`);
      }
    } catch (err) { console.error('[Reaction]', err); }
  };

  const handlePreview = (url: string) => {
    // Classify by the attachment's MIME/filename, not by string equality with
    // `att.url`. The old check (`a.url === url && mimeType === 'application/pdf'`)
    // silently mis-routed PDFs whose mimeType was generic/legacy — and every
    // already-decoded image, which arrives as a blob: URL — into the image
    // lightbox, which then reported the file as unloadable.
    const att = signedAttachments.find(a => a.url === url);
    if (isPdfAttachment(att, url)) {
      setPreviewPdf({ url, fileName: att?.fileName || 'document.pdf' });
      return;
    }
    if (isImageAttachment(att, url) || isDirectPreviewUrl(url)) {
      setPreviewImage({ url, fileName: att?.fileName || 'Image' });
      return;
    }
    // Word / Excel / archives can't be rendered in-app: hand over the file
    // rather than opening a popup that can only say "unavailable".
    void downloadFile(url, att?.fileName).catch(() => { /* downloadFile logs */ });
  };

  const reactionEntries = Object.entries(localReactions || {}).filter(([, users]) => (users as string[]).length > 0);
  const hasImages = signedImageAttachments.length > 0;
  const hasDocs = signedDocAttachments.length > 0;

  const avatarColor = comment.isInternal
    ? AVATOR_COLORS.internal
    : comment.authorRole === 'admin'
      ? AVATOR_COLORS.admin
      : AVATOR_COLORS.applicant;

  // Internal notes are team-only: they take precedence over the own/other
  // pairing so an admin's own note can never look like a reply they just sent
  // to the applicant.
  const isInternalNote = !!comment.isInternal;

  // Shared design-system bubble tones (see src/components/messaging/messaging.css)
  const bubbleStyle = isInternalNote
    ? 'msg-bubble msg-bubble--note'
    : isOwn
      ? 'msg-bubble msg-bubble--own'
      : 'msg-bubble msg-bubble--other';

  const messageActions: MessageAction[] = [
    { id: 'react', label: 'React', icon: <SmilePlus className="h-4 w-4" />, action: () => setShowReactions(true) },
    ...(onReply ? [{ id: 'reply', label: 'Reply', icon: <Reply className="h-4 w-4" />, action: () => onReply(comment) }] : []),
    { id: 'copy', label: 'Copy text', icon: <Copy className="h-4 w-4" />, action: () => navigator.clipboard.writeText(bodyText) },
    ...(canEdit && onEdit ? [{ id: 'edit', label: 'Edit', icon: <Edit3 className="h-4 w-4" />, action: () => onEdit(comment) }] : []),
    ...(canDelete && onDelete ? [{ id: 'delete', label: 'Delete', icon: <Trash2 className="h-4 w-4" />, action: () => onDelete(comment) }] : []),
  ];

  return (
    <>
      {/*
       * OUTER ROW
       * — DO NOT add overflow-hidden here; that pushes absolute popups
       *   into the scroll-width calculation on Safari/Chrome mobile.
       * — `contain: layout` (via style) isolates absolute children from
       *   the document flow without clipping them.
       * — `w-full min-w-0` + `box-sizing: border-box` keeps the row within
       *   the parent container regardless of content size.
       */}
      <div
        className={[
          'flex gap-1.5 mb-1.5 relative group',
          'w-full min-w-0',
          // Own public replies mirror to the right; internal notes stay
          // left-aligned on both sides so they read as team-only notes rather
          // than as part of the customer↔agent flow.
          isOwn && !isInternalNote ? 'flex-row-reverse' : '',
          comment.isInternal ? 'opacity-85' : '',
        ].join(' ')}
        style={{ contain: 'layout', boxSizing: 'border-box' }}
        role="listitem"
        onContextMenu={(e) => { e.preventDefault(); setShowActions(true); }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchEnd}
      >
        {/* Avatar column — rendered for BOTH sides immediately adjacent to bubble */}
        <div className="shrink-0 self-end flex flex-col items-center w-6">
          {!hideAvatar ? (
            <Avatar
              name={comment.authorName || (isOwn ? 'You' : 'Unknown')}
              size="xs"
              tone={avatarColor}
            />
          ) : (
            <div className="w-6" />
          )}
        </div>

        {/* CONTENT COLUMN */}
        <div
          className={[
            'flex flex-col min-w-0',
            isOwn && !isInternalNote ? 'items-end' : 'items-start',
          ].join(' ')}
          style={{
            maxWidth: 'calc(100% - 2rem)',
          }}
        >
          {/* Author header — every message, both directions, so it is always
              obvious WHO sent it and WHICH SIDE they are on. */}
          {showHeader && (
            <div className={`flex items-center flex-wrap gap-y-0.5 gap-x-1.5 mb-1 px-1 min-w-0 max-w-full overflow-hidden ${
              isOwn && !isInternalNote ? 'justify-end' : ''
            }`}>
              <span className="text-[12px] font-semibold text-slate-500 truncate">
                {comment.authorName || (isOwn ? 'You' : 'Unknown sender')}
              </span>
              {isInternalNote && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0 leading-none">
                  <Lock className="h-2.5 w-2.5" aria-hidden="true" /> Internal note
                </span>
              )}
              {!isInternalNote && comment.authorRole === 'applicant' && (
                <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold leading-none text-slate-600">
                  Applicant
                </span>
              )}
              {!isInternalNote && comment.authorRole !== 'applicant' && (
                <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-bold leading-none text-blue-700">
                  Team
                </span>
              )}
              {comment.unverifiedSender && (
                <span className="text-[11px] font-bold text-rose-700 bg-rose-100 border border-rose-200 px-1.5 py-0.5 rounded-full shrink-0 leading-none" title="This email reply came from a sender whose email address does not match the applicant on file. Please double-check identity before responding.">Unverified Sender</span>
              )}
            </div>
          )}
          {/* Internal badge when the header isn't rendered (grouped messages) */}
          {isInternalNote && !showHeader && (
            <div className={`flex items-center flex-wrap gap-y-0.5 gap-x-1.5 mb-0.5 px-1 ${isOwn ? 'justify-end' : ''}`}>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0 rounded-full">
                <Lock className="h-2.5 w-2.5" aria-hidden="true" /> Internal note
              </span>
            </div>
          )}

          {/*
           * BUBBLE
           * — `max-w-full` stops it from ever being wider than the content column.
           * — `overflow-hidden` clips attachment cards that escape.
           * — `break-words` + overflowWrap:'anywhere' wraps long URLs on iOS.
           * — Do NOT use `w-full` here — it would make short messages full-width;
           *   the bubble should hug its content naturally.
           */}
          <div
            className={[
              'px-3 py-1.5 sm:px-3.5 sm:py-2 text-[13.5px] sm:text-[14px] leading-[1.4] whitespace-pre-wrap rounded-2xl shadow-xs',
              'max-w-full overflow-hidden break-words',
              bubbleStyle,
            ].join(' ')}
            style={{ overflowWrap: 'anywhere' }}
            title={formatExactDateTime(comment.createdAt)}
          >
            {/* Quoted message */}
            {comment.quotedMessage && (
              <QuotedMessageBlock
                authorName={comment.quotedMessage.authorName}
                body={comment.quotedMessage.body}
                commentId={(comment.quotedMessage as any)?.commentId || comment.parentId}
                isOwn={isOwn}
                onJumpToMessage={onJumpToMessage}
              />
            )}

            {/* Message body.
                The "(file attachment)" placeholder is never shown as text, and
                an attachment error is rendered as a CARD (icon + label), never
                as if the admin had typed that sentence and sent it. */}
            {isPlaceholderBody ? (
              !hasAttachments && (
                <span className={`inline-flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-[12px] font-medium ${
                  isOwn
                    ? 'border-white/25 bg-white/10 text-white/80'
                    : 'border-slate-300/70 bg-slate-50 text-slate-500'
                }`}>
                  <FileX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  This file was removed
                </span>
              )
            ) : (
              !!bodyText && (highlightQuery ? highlightText(bodyText, highlightQuery) : bodyText)
            )}

            {/* Image grid */}
            {hasImages && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {signedImageAttachments.map((att, i) => (
                  <ImageAttachment key={i} url={att.url} fileName={att.fileName} onPreview={handlePreview} />
                ))}
              </div>
            )}

            {/* Document attachments */}
            {hasDocs && (
              <div className="mt-2 space-y-1 w-full">
                {signedDocAttachments.map((att, i) =>
                  isOwn ? (
                    <AttachmentCardLight key={i} att={att} onPreview={handlePreview} />
                  ) : (
                    <AttachmentCardReceived key={i} att={att} onPreview={handlePreview} />
                  )
                )}
              </div>
            )}

            {/* Timestamp */}
            <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <span className={`text-[10px] font-semibold tracking-wide tabular-nums ${isOwn ? 'text-white/75' : 'text-slate-400'}`} title={formatExactDateTime(comment.createdAt)} aria-label={`Sent ${formatExactDateTime(comment.createdAt)}`}>{formatFullTime(comment.createdAt)}</span>
              {comment.editedAt && <span className={`text-[10px] italic font-semibold ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>· edited</span>}
              {comment.status === 'failed' ? (
                <div className="flex items-center gap-1 text-red-200 font-bold select-none shrink-0" title="Message failed to send">
                  <AlertCircle className="h-3.5 w-3.5 text-red-300" />
                  <span>Failed</span>
                </div>
              ) : isOwn ? (
                <span
                  className="text-[10px] font-bold ml-1 select-none shrink-0 text-blue-200"
                  title={comment.isInternal ? 'Internal note — visible to admins only' : 'Sent to the applicant'}
                  aria-label={comment.isInternal ? 'Internal note' : 'Sent'}
                >
                  ✓
                </span>
              ) : null}
            </div>
          </div>

          {/*
           * REACTIONS ROW
           * — `max-w-full overflow-hidden` prevents emoji buttons pushing the
           *   row wider than the bubble on narrow screens.
           * — Reaction buttons use p-1.5 (not min-w/h-[44px]) inside this row
           *   so they don't expand the row; touch target is still fine since
           *   the row itself is tall enough.
           */}
          <div className={[
            'flex items-center gap-1 mt-0.5 max-w-full overflow-hidden flex-wrap',
            isOwn ? 'flex-row-reverse' : '',
          ].join(' ')}>
            {comment.status === 'failed' && onRetry && (
              <button
                onClick={() => onRetry(comment)}
                className="text-xs font-black text-red-500 hover:text-red-650 bg-red-50 px-2.5 py-1 rounded-full border border-red-150 transition active:scale-95 cursor-pointer min-h-[32px] flex items-center gap-1"
                type="button"
              >
                <RefreshCw className="h-3 w-3" />
                <span>Try Again</span>
              </button>
            )}
            {reactionEntries.length > 0 && (
              <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-full px-1.5 py-0.5 shadow-sm shrink-0" role="group" aria-label="Reactions">
                {reactionEntries.map(([emoji, users]) => {
                  const userList = users as string[];
                  return (
                    <button
                      key={emoji}
                      onClick={() => handleReaction(emoji as TicketReaction)}
                      className="flex items-center justify-center gap-0.5 hover:bg-slate-100 rounded-full px-1 py-0.5 transition-all cursor-pointer min-w-[44px] min-h-[44px]"
                      title={userList.join(', ')}
                      type="button"
                    >
                      <span className="text-sm">{emoji}</span>
                      <span className="text-[10px] text-slate-500 font-semibold">{userList.length}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Quick reply button — visible on hover alongside reaction */}
            {onReply && (
              <button
                onClick={() => onReply(comment)}
                className="text-slate-400 hover:text-blue-500 hover:bg-slate-100 rounded-full p-1.5 transition-all cursor-pointer flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 shrink-0 min-w-[44px] min-h-[44px]"
                title="Reply"
                aria-label="Reply to this message"
                type="button"
              >
                <Reply className="h-4 w-4" />
              </button>
            )}

            {/* Add reaction button */}
            <div className="relative shrink-0" data-reaction-picker-root>
              <button
                onClick={() => setShowReactions(!showReactions)}
                className="text-slate-400 hover:text-blue-500 hover:bg-slate-100 rounded-full p-1.5 transition-all cursor-pointer flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 shrink-0 min-w-[44px] min-h-[44px]"
                title="Add reaction"
                aria-label="Add reaction"
                type="button"
              >
                <SmilePlus className="h-4 w-4" />
              </button>
              {showReactions && (
                <ReactionPicker
                  onReact={handleReaction}
                  onClose={() => setShowReactions(false)}
                  isOwn={isOwn}
                />
              )}
            </div>

            {/* More actions (three dots) */}
            <button
              onClick={() => setShowActions(!showActions)}
              className="text-slate-400 hover:text-blue-500 hover:bg-slate-100 rounded-full p-1.5 transition-all cursor-pointer flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 shrink-0 min-w-[44px] min-h-[44px]"
              title="More actions"
              aria-label="More actions"
              type="button"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/*
         * ACTIONS MENU — kept inside the row div so it can use relative positioning,
         * but it is absolute and z-indexed so it floats above everything without
         * affecting layout width. The `contain: layout` on the row prevents it
         * from escaping into the scroll-width calculation.
         */}
        {showActions && (
          <MessageActionsMenu
            actions={messageActions}
            onClose={() => setShowActions(false)}
            isOwn={isOwn}
          />
        )}
      </div>

      {previewImage && (
        <ImageLightbox
          url={previewImage.url}
          fileName={previewImage.fileName}
          onClose={() => setPreviewImage(null)}
        />
      )}
      {previewPdf && (
        <PdfPreviewModal url={previewPdf.url} fileName={previewPdf.fileName} onClose={() => setPreviewPdf(null)} />
      )}
    </>
  );
});

ChatBubble.displayName = 'ChatBubble';

/* ── Typing Indicator ── */

export function TypingIndicator({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  const label = names.length === 1 ? `${names[0]} is typing...` : `${names.join(', ')} are typing...`;
  return (
    <div className="flex items-center gap-2 mb-1 self-start max-w-[70%]" role="status" aria-label={label}>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Audit Event Chip ── */

const AUDIT_PREFIXES = ['Status changed', 'Priority changed', 'Assigned to', 'Title updated', 'Description updated', 'Archived', 'Restored from', 'Category changed', 'Category updated'];
export function isAuditComment(c: TicketComment): c is TicketComment {
  return c.isInternal && AUDIT_PREFIXES.some(p => c.body.startsWith(p));
}

export function AuditChip({ comment }: { comment: TicketComment } & Record<string, unknown>) {
  return (
    <div className="flex items-center justify-center my-2" role="status">
      <div className="flex items-center gap-1.5 text-slate-400 bg-white/80 backdrop-blur-sm border border-slate-200/70 rounded-full px-3.5 py-1.5 shadow-sm">
        <span className="font-semibold text-slate-500 text-[11px]">{comment.authorName}</span>
        <span className="text-[11px]">{messageBodyText(comment.body)}</span>
        <span className="text-slate-300">·</span>
        <span className="text-[10px]">{formatRelativeTime(comment.createdAt)}</span>
      </div>
    </div>
  );
}

/* ── Jump to Latest Button ── */

export function JumpToLatestButton({ onClick, hidden }: { onClick: () => void; hidden: boolean }) {
  React.useEffect(() => { ensureStyleInjected(); }, []);
  if (hidden) return null;
  return (
    <button
      onClick={onClick}
      className="absolute bottom-16 sm:bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 shadow-xl hover:shadow-lg hover:border-blue-300 hover:bg-white transition-all cursor-pointer animate-slide-up min-h-[44px]"
      aria-label="Jump to latest message"
      type="button"
    >
      <ChevronDown className="h-4 w-4 text-blue-500" />
      <span className="text-xs font-bold text-brand">New messages</span>
    </button>
  );
}

/* ── Chat Message Input ── */

interface ChatMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
  disabled?: boolean;
  placeholder?: string;
  onAttachFiles?: (files: File[]) => void;
  pendingFiles?: { name: string; size: number; mimeType?: string; previewUrl?: string }[];
  onRemoveFile?: (index: number) => void;
  isInternal?: boolean;
  onInternalToggle?: (checked: boolean) => void;
  showInternalToggle?: boolean;
  children?: React.ReactNode;
  uploadProgress?: number | null;
  onCancelUpload?: () => void;
  /** Retry a failed upload/send (shows a Retry action in the failure banner). */
  onRetryUpload?: () => void;
}

export function ChatMessageInput({
  value, onChange, onSend, sending, disabled,
  placeholder = 'Write a reply...',
  onAttachFiles, pendingFiles, onRemoveFile,
  isInternal, onInternalToggle, showInternalToggle,
  children, uploadProgress, onCancelUpload, onRetryUpload,
}: ChatMessageInputProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const composerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const vv = window.visualViewport;
      if (!vv) return;
      const handle = () => {
        if (composerRef.current) {
          const isMobile = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
          if (!isMobile) {
            composerRef.current.style.transform = '';
            return;
          }
          const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          if (diff > 30) {
            composerRef.current.style.transform = `translateY(-${diff}px)`;
          } else {
            composerRef.current.style.transform = '';
          }
        }
      };
      vv.addEventListener('resize', handle);
      vv.addEventListener('scroll', handle);
      handle();
      return () => { vv.removeEventListener('resize', handle); vv.removeEventListener('scroll', handle); };
    }, []);

  const isTouchDevice = React.useRef(
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl+Enter always sends — including on touch devices, where plain
    // Enter inserts a newline instead.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSend(); return; }
    if (e.key === 'Enter' && !e.shiftKey && !isTouchDevice.current) { e.preventDefault(); onSend(); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onAttachFiles) onAttachFiles(Array.from(files));
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const [dragOver, setDragOver] = React.useState(false);
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files) as File[];
    if (files.length > 0 && onAttachFiles) onAttachFiles(files);
  };

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [value]);

  React.useEffect(() => {
    const handleFocus = () => setTimeout(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 350);
    const textarea = textareaRef.current;
    textarea?.addEventListener('focus', handleFocus);
    return () => textarea?.removeEventListener('focus', handleFocus);
  }, []);

  const canSend = value.trim() !== '' || (pendingFiles && pendingFiles.length > 0);

  return (
    <div
      ref={composerRef}
      className={`border-t sticky bottom-0 z-[var(--z-sticky)] w-full transition-colors ${
        isInternal ? 'border-amber-300 bg-amber-50' : 'border-slate-200/70 bg-white'
      }`}
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Trust-and-safety: make the active mode unmistakable BEFORE sending, so
          nobody fat-fingers an internal note into a customer-facing reply (or
          the reverse). Amber is the industry convention (Intercom/Zendesk/Front). */}
      {isInternal && showInternalToggle && (
        <div className="flex items-center gap-1.5 px-3 pt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-amber-800">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Internal note — the applicant can&apos;t see this
        </div>
      )}
      {dragOver && (
        <div className="absolute inset-0 z-20 bg-brand-primary/10 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-1">
            <Upload className="h-6 w-6 text-blue-500" />
            <p className="text-xs font-bold text-brand">Drop files here</p>
          </div>
        </div>
      )}

      {uploadProgress != null && (
        <div className={`px-4 py-1.5 border-b ${
          uploadProgress >= 100 ? 'bg-emerald-50 border-emerald-100'
            : uploadProgress < 0 ? 'bg-rose-50 border-rose-100'
            : 'bg-blue-50 border-blue-100'
        }`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              {uploadProgress >= 100 ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-[11px] font-semibold text-emerald-700">Upload complete</span>
                </div>
              ) : uploadProgress < 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertCircle className="h-4 w-4 text-rose-500 shrink-0" />
                  <span className="text-[11px] font-semibold text-rose-700">Upload failed</span>
                  {onRetryUpload && (
                    <button onClick={onRetryUpload} className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-all cursor-pointer min-h-[32px]" type="button">
                      <RefreshCw className="h-3 w-3" /> Retry
                    </button>
                  )}
                  {onCancelUpload && (
                    <button onClick={onCancelUpload} className="px-2.5 py-1 text-[10px] font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-all cursor-pointer min-h-[32px]" type="button">
                      Dismiss
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-primary rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-brand shrink-0">{uploadProgress}%</span>
                </div>
              )}
            </div>
            {uploadProgress > 0 && uploadProgress < 100 && onCancelUpload && (
              <button onClick={onCancelUpload} className="p-1 text-slate-400 hover:text-rose-500 transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center" type="button" aria-label="Cancel upload">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {pendingFiles && pendingFiles.length > 0 && (
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {pendingFiles.map((f, i) => {
              const mime = (f.mimeType || '').toLowerCase();
              const isImage = mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name);
              return (
                <div key={i} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs shadow-sm shrink-0 min-w-0">
                  {isImage && f.previewUrl ? (
                    <img src={f.previewUrl} alt="" className="w-8 h-8 rounded-lg object-cover border border-slate-100 shrink-0" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getFileColor(mime || (f.name.endsWith('.pdf') ? 'application/pdf' : ''))} flex items-center justify-center shrink-0 shadow-xs`}>
                      {getFileIcon(mime || (f.name.endsWith('.pdf') ? 'application/pdf' : ''), 'h-4 w-4 text-white')}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-700 truncate max-w-[110px]" title={f.name}>{middleTruncateFileName(f.name, 20)}</p>
                    <p className="text-[10px] text-slate-400">{formatFileSize(f.size)} · {getFileLabel(mime || (f.name.endsWith('.pdf') ? 'application/pdf' : ''))}</p>
                  </div>
                  <button
                    onClick={() => onRemoveFile?.(i)}
                    className="shrink-0 p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                    type="button"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-3 py-2 sm:px-4 sm:py-3 min-w-0">
        <div className="composer-bar flex flex-wrap items-center gap-2 min-w-0">
          {onAttachFiles && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="msg-composer-icon-btn shrink-0"
                title="Attach a file (PDF, Word, Excel, CSV, TXT, RTF, JPEG, PNG, GIF, WEBP, HEIC — max 10 MB)"
                aria-label="Attach a file"
                disabled={sending || disabled}
                type="button"
              >
                <Paperclip className="h-4 w-4" aria-hidden="true" />
              </button>
              <input ref={fileInputRef} type="file" accept={ALLOWED_EXTENSIONS} multiple onChange={handleFileSelect} className="hidden" aria-hidden="true" tabIndex={-1} />
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="msg-composer-icon-btn shrink-0 sm:hidden"
                title="Take a photo and attach it"
                aria-label="Take a photo"
                disabled={sending || disabled}
                type="button"
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
              </button>
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" aria-hidden="true" tabIndex={-1} />
            </div>
          )}

          {children && (
            <div className="flex items-center gap-1 shrink-0">
              {children}
            </div>
          )}

          <div className="composer-input flex-1 min-w-[190px] relative">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isInternal ? "Write an internal note..." : placeholder}
              inputMode="text"
              enterKeyHint="send"
              className={`w-full border rounded-xl px-3.5 py-2 text-[14px] outline-none resize-none min-h-[40px] max-h-[140px] leading-relaxed transition-all duration-200 ${
                isInternal
                  ? 'border-amber-300 bg-amber-100/50 focus:border-amber-400 focus:bg-white text-amber-950 placeholder:text-amber-700/60'
                  : 'border-slate-200 bg-slate-50 hover:bg-white focus:bg-white focus:border-brand/40 focus:ring-2 focus:ring-brand/10 text-slate-900'
              }`}
              style={{ fontSize: '16px' }}
              rows={1}
              aria-label="Message text"
              disabled={sending || disabled}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                const maxH = Math.min(160, (window.visualViewport?.height ?? 400) * 0.28);
                el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
              }}
              onFocus={() => setTimeout(() => textareaRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 350)}
            />
          </div>

          <button
            onClick={onSend}
            disabled={!canSend || sending || disabled}
            className={`msg-send-btn shrink-0 ${isInternal ? 'msg-send-btn--note' : ''}`}
            aria-label={isInternal ? "Save internal note" : "Send message"}
            title={isInternal ? "Save internal note (the applicant can't see it)" : "Send message (Ctrl/⌘ + Enter)"}
            type="button"
          >
            {uploadProgress != null && uploadProgress > 0 && uploadProgress < 100 ? (
              <span className="text-[10px] font-bold">{uploadProgress}%</span>
            ) : sending ? (
              <>
                <Loader className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                <span>Sending</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{isInternal ? 'Add note' : 'Send'}</span>
              </>
            )}
          </button>
        </div>
        <p className="mt-1.5 ml-1 hidden flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-medium text-slate-500 sm:flex">
          <span>PDF, Word, Excel, CSV, TXT, RTF, images</span>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <span className="font-semibold text-slate-600">Max 10 MB per file</span>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <span>Enter to send, Shift + Enter for a new line</span>
        </p>
      </div>
    </div>
  );
}

/* ── Empty Chat State ── */

export function EmptyChatState() {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-10">
      <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
        <MessageSquare className="h-7 w-7 text-blue-400" />
      </div>
      <p className="text-[15px] font-bold text-slate-700 mb-1">No messages yet</p>
      <p className="text-[13px] text-slate-400 max-w-[200px] leading-relaxed">Send a message below to start the conversation.</p>
    </div>
  );
}

/* ── Auto-scroll manager ── */

interface AutoScrollHandle {
  endRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  onScroll: () => void;
  isNearBottom: boolean;
  scrollToBottom: (smooth?: boolean) => void;
}

/**
 * Auto-scroll manager hook.
 *
 * When `autoScroll` is true (default), the view auto-scrolls to the bottom
 * whenever `deps` change and the user is near the bottom.
 * When `autoScroll` is false, the user must manually click a scroll-to-bottom
 * button — useful for admin chat where reading history and polling for new
 * messages should not push the user down.
 */
export function useAutoScrollManager(deps: any[], autoScroll = true): AutoScrollHandle {
  const endRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLElement | null>(null);
  const [isNearBottom, setIsNearBottom] = React.useState(true);
  const isUserScrolling = React.useRef(false);

  const onScroll = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const threshold = 150;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setIsNearBottom(atBottom);
    if (atBottom) isUserScrolling.current = false;
  }, []);

  const scrollToBottom = React.useCallback((smooth = true) => {
    endRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    setIsNearBottom(true);
    isUserScrolling.current = false;
  }, []);

  React.useEffect(() => {
    const container = endRef.current?.parentElement ?? null;
    if (!container) return;
    containerRef.current = container;
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  React.useEffect(() => {
    if (!autoScroll) return;
    if (isNearBottom) {
      const timer = setTimeout(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 60);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { endRef, containerRef, onScroll, isNearBottom, scrollToBottom };
}

/* ── QuotedMessageBlock ── */

interface QuotedMessageBlockProps {
  authorName: string;
  body: string;
  isOwn: boolean;
  commentId?: string;
  onJumpToMessage?: (commentId?: string, authorName?: string, body?: string) => void;
}

export const QuotedMessageBlock = React.memo(({ authorName, body, isOwn, commentId, onJumpToMessage }: QuotedMessageBlockProps) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      if (onJumpToMessage) onJumpToMessage(commentId, authorName, body);
    }}
    className={`w-full text-left mb-1.5 pl-2.5 pr-2 py-1 border-l-3 rounded-lg text-xs transition-all cursor-pointer group/quote select-none ${
      isOwn
        ? 'border-blue-300 bg-white/15 hover:bg-white/25'
        : 'border-blue-500 bg-slate-100/90 hover:bg-slate-200/90'
    }`}
    title="Click to jump to original message"
  >
    <div className="flex items-center justify-between gap-1">
      <span className={`block text-[10px] font-bold leading-tight ${isOwn ? 'text-blue-100' : 'text-blue-600'}`}>
        {authorName}
      </span>
      <CornerDownRight className={`h-3 w-3 opacity-60 group-hover/quote:opacity-100 transition-opacity shrink-0 ${isOwn ? 'text-blue-100' : 'text-blue-500'}`} aria-hidden="true" />
    </div>
    <span className={`block leading-snug line-clamp-2 ${isOwn ? 'text-blue-100/90' : 'text-slate-600'}`}>
      {body}
    </span>
  </button>
));

QuotedMessageBlock.displayName = 'QuotedMessageBlock';

/* ── ReplyBar / EditBar ── */

interface ReplyBarProps { authorName: string; body: string; onCancel: () => void; }

export function ReplyBar({ authorName, body, onCancel }: ReplyBarProps) {
  return (
    <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-blue-50 border-t border-blue-100">
      <div className="flex-1 min-w-0">
        <span className="block text-[10px] md:text-[11px] font-bold text-brand leading-tight">Replying to {authorName}</span>
        <span className="block text-[11px] md:text-xs text-slate-500 truncate leading-snug">{body}</span>
      </div>
      <button
        onClick={onCancel}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-blue-100 rounded-lg transition-all cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
        aria-label="Cancel reply"
        type="button"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function EditBar({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-amber-50 border-t border-amber-100">
      <div className="flex-1 min-w-0">
        <span className="block text-[10px] font-bold text-amber-600 leading-tight">Editing message</span>
      </div>
      <button
        onClick={onCancel}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-amber-100 rounded-lg transition-all cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
        aria-label="Cancel editing"
        type="button"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ── highlightText ── */

export function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} role="mark" className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>
      : part,
  );
}

/* ── ChatTabStrip ── */

export type TicketChatTab = 'conversation' | 'internal' | 'docs';

interface TabStripProps {
  activeTab: TicketChatTab;
  onTabChange: (tab: TicketChatTab) => void;
  docCount: number;
  internalCount?: number;
  showInternalTab?: boolean;
}

export function ChatTabStrip({ activeTab, onTabChange, docCount, internalCount = 0, showInternalTab = false }: TabStripProps) {
  const TABS = [
    { id: 'conversation' as const, label: 'Conversation' },
    ...(showInternalTab ? [{ id: 'internal' as const, label: `Internal Notes${internalCount > 0 ? ` (${internalCount})` : ''}` }] : []),
    { id: 'docs' as const, label: `Documents${docCount > 0 ? ` (${docCount})` : ''}` },
  ];
  return (
    <div className="relative shrink-0 border-b border-slate-200 bg-white px-3 py-2" role="tablist">
      <div className="msg-tabstrip">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          // Internal Notes carries its own warm active state: an admin must never
          // have to re-read the label to know whether they are in the
          // customer-facing thread or the team-only one.
          const stateClass = !isActive
            ? ''
            : tab.id === 'internal' ? 'msg-tab--active-note' : 'msg-tab--active';
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={`msg-tab ${stateClass}`}
              type="button"
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── SearchBar ── */

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  resultCount: number;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

export function SearchBar({ value, onChange, resultCount, currentIndex, onPrev, onNext, onClose }: SearchBarProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => { inputRef.current?.focus(); }, []);
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const handlePopState = () => { onClose(); };
    document.addEventListener('keydown', handler);
    window.addEventListener('popstate', handlePopState);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-200">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Search messages…"
          aria-label="Search messages"
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-brand-primary bg-slate-50"
          style={{ fontSize: '16px' }}
        />
      </div>
      {resultCount > 0 && (
        <span className="text-[11px] md:text-xs text-slate-500 font-semibold shrink-0 tabular-nums">
          {currentIndex + 1} of {resultCount}
        </span>
      )}
      <div className="flex items-center gap-0.5 shrink-0">
        <button onClick={onPrev} disabled={resultCount === 0} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Previous match" type="button">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button onClick={onNext} disabled={resultCount === 0} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Next match" type="button">
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
      <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close search" type="button">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ── DocumentsPanel ── */

interface DocumentsPanelProps {
  comments: any[];
  currentUserId?: string;
  includeInternal?: boolean;
  ticketId?: string;
  /**
   * Ticket-level attachments written before per-comment attachments existed
   * (`Ticket.attachments: {name,url}[]`). They live on the ticket, not on a
   * comment, so this panel never saw them and those files appeared to be gone.
   */
  legacyAttachments?: { name?: string; url?: string }[] | null;
}

function useAttachments(
  comments: any[],
  includeInternal: boolean,
  ticketId?: string,
  legacyAttachments?: { name?: string; url?: string }[] | null,
) {
  return React.useMemo(() => {
    const images: { fileId: string; url: string; fileName: string; fileSize: number; senderName: string; senderId: string; createdAt: string }[] = [];
    const docs: { fileId: string; url: string; fileName: string; fileSize: number; mimeType: string; status?: TicketAttachment['status']; senderName: string; senderId: string; createdAt: string; isInternal?: boolean }[] = [];
    const seen = new Set<string>();
    for (const c of comments) {
      if (c.isInternal && !includeInternal) continue;
      if (!c.attachments || !Array.isArray(c.attachments)) continue;
      for (const rawAttachment of c.attachments) {
        const att = normalizeTicketAttachment(rawAttachment);
        if (!att) continue;
        if (seen.has(att.fileId)) continue;
        seen.add(att.fileId);
        // Resolve a displayable URL, and never drop a record silently.
        //
        // The storage-upload path persists `storagePath` plus a server-minted
        // `url`; partial or older records can carry only one of the two, and
        // the old `if (!att.url) continue;` threw those away — which is exactly
        // how documents went missing from this panel. A storage path's basename
        // IS the fileId the /api/uploads proxy expects, so it is recoverable.
        const rawUrl = att.url;
        const fileName = att.fileName;
        const mimeType = att.mimeType;
        const entry = {
          fileId: att.fileId,
          url: ticketAttachmentUrl(rawUrl, ticketId),
          fileName,
          fileSize: Number(att?.fileSize ?? (att as any)?.size ?? 0) || 0,
          senderName: c.authorName || 'Unknown',
          senderId: c.authorId || '',
          createdAt: c.createdAt || '',
        };
        // Classify by MIME *and* extension so a legacy record with a generic
        // mimeType still lands in the Images grid instead of the Documents list.
        if (att.status === 'available' && isImageAttachment({ mimeType, fileName }, rawUrl)) images.push(entry);
        else docs.push({ ...entry, mimeType, status: att.status, isInternal: !!c.isInternal });
      }
    }
    // Ticket-level legacy attachments last, so they never outrank live messages.
    for (const legacy of (legacyAttachments || [])) {
      if (!legacy?.url) continue;
      const fileName = legacy.name || 'Attachment';
      const legacyId = legacy.url.split('?')[0].split('/').pop() || fileName;
      if (seen.has(legacyId)) continue;
      seen.add(legacyId);
      docs.push({
        fileId: legacyId,
        url: ticketAttachmentUrl(legacy.url, ticketId),
        fileName,
        fileSize: 0,
        mimeType: 'application/octet-stream',
        senderName: 'Ticket attachment',
        senderId: '',
        createdAt: '',
      });
    }
    return { images, docs };
  }, [comments, includeInternal, ticketId, legacyAttachments]);
}

export function DocumentsPanel({
  comments, currentUserId, includeInternal = false, ticketId, legacyAttachments,
}: DocumentsPanelProps) {
  const { images, docs } = useAttachments(comments, includeInternal, ticketId, legacyAttachments);
  const [previewImage, setPreviewImage] = React.useState<{ url: string; fileName: string } | null>(null);
  const [previewPdf, setPreviewPdf] = React.useState<{ url: string; fileName: string } | null>(null);

  /**
   * One router for every "View" affordance in this panel.
   *
   * PDFs open in the PDF viewer, images in the lightbox, and anything else
   * (Word / Excel / archives, which no browser can render) downloads instead of
   * opening a popup that can only ever say "unavailable" — the old code sent
   * every non-PDF to the image lightbox, which is why a .docx "preview" failed.
   */
  const openAttachmentPreview = (att: { url: string; fileName: string; mimeType: string }) => {
    if (isPdfAttachment(att, att.url)) {
      setPreviewPdf({ url: att.url, fileName: att.fileName });
      return;
    }
    if (isImageAttachment(att, att.url) || isDirectPreviewUrl(att.url)) {
      setPreviewImage({ url: att.url, fileName: att.fileName });
      return;
    }
    void downloadFile(att.url, att.fileName).catch(() => { /* downloadFile logs */ });
  };

  if (images.length === 0 && docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-6">
        <div className="bg-slate-100 rounded-full p-4 mb-3"><File className="h-8 w-8 text-slate-400" /></div>
        <p className="text-sm font-bold text-slate-500">No files shared yet</p>
        <p className="text-xs text-slate-400 mt-1">Files attached to messages will appear here.</p>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 sm:px-5">
      {images.length > 0 && (
        <div className="mb-5">
          <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Image className="h-3.5 w-3.5" /> Images · {images.length}
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((img, i) => (
              <AuthThumb
                key={`img-${i}`}
                url={img.url}
                fileName={img.fileName}
                meta={`${img.senderName} · ${formatRelativeTime(img.createdAt)}`}
                alt={img.fileName}
                onClick={(resolvedUrl) => setPreviewImage({ url: resolvedUrl, fileName: img.fileName })}
              />
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div>
          <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Documents · {docs.length}
          </h3>
          <div className="space-y-2">
            {docs.map((doc, i) => {
              const url = doc.url;
              const isOwn = currentUserId ? doc.senderId === currentUserId : false;
              return (
                <div key={`doc-${i}`}>
                  {isOwn ? (
                    <AttachmentCardPanelOwn
                      att={{ fileId: doc.fileId, url, fileName: doc.fileName, fileSize: doc.fileSize, mimeType: doc.mimeType, status: doc.status } as CommentAttachment}
                      onPreview={(u) => openAttachmentPreview({ url: u, fileName: doc.fileName, mimeType: doc.mimeType })}
                    />
                  ) : (
                    <AttachmentCardReceived
                      att={{ fileId: doc.fileId, url, fileName: doc.fileName, fileSize: doc.fileSize, mimeType: doc.mimeType, status: doc.status } as CommentAttachment}
                      onPreview={(u) => openAttachmentPreview({ url: u, fileName: doc.fileName, mimeType: doc.mimeType })}
                    />
                  )}
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mt-0.5 ml-1">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="font-semibold text-slate-500 truncate">{doc.senderName}</span>
                    {doc.isInternal && <span className="rounded-full bg-amber-100 px-1.5 py-0 text-[11px] font-bold text-amber-800">Internal</span>}
                    <span className="text-slate-300">·</span>
                    <span>{formatRelativeTime(doc.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {previewImage && (
        <ImageLightbox
          url={previewImage.url}
          fileName={previewImage.fileName}
          onClose={() => setPreviewImage(null)}
        />
      )}
      {previewPdf && <PdfPreviewModal url={previewPdf.url} fileName={previewPdf.fileName} onClose={() => setPreviewPdf(null)} />}
    </div>
  );
}
